import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateNcProvincialContributions, UpdateNcProvincialContributions } from '@repo/backend/crt'
import {
  MONTH_KEYS,
  NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERROR_TYPES,
  PRODUCT_CATEGORIES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const ncProvincialContributionsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const NC_PROVINCIAL_CONTRIBUTIONS_YEAR_VALUES = ['cy', 'ny'] as const
export type NcProvincialContributionsYear = (typeof NC_PROVINCIAL_CONTRIBUTIONS_YEAR_VALUES)[number]

export const ncProvincialContributionsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  taxProvince: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  month: z.enum(MONTH_KEYS).optional(),
  year: z.enum(NC_PROVINCIAL_CONTRIBUTIONS_YEAR_VALUES).optional(),
  ...datatableSearchSchema.shape,
})

export type NcProvincialContributionsSearch = z.infer<typeof ncProvincialContributionsSearchSchema>
export type NcProvincialContributionsDetailsSearch = z.infer<
  typeof ncProvincialContributionsDetailsSearchSchema
>

export type NcProvincialContributionsDetailsServerQuery = Omit<
  NcProvincialContributionsDetailsSearch,
  'month' | 'year'
>

export type NcProvincialContributionsList = Awaited<
  ReturnType<typeof getNcProvincialContributionsList>
>
export type NcProvincialContributions = NcProvincialContributionsList['results'][number]
export type NcProvincialContributionsWithDetails = Awaited<
  ReturnType<typeof getNcProvincialContributionsById>
>

type UseCreateNcProvincialContributionsArgs = {
  queries: NcProvincialContributionsSearch
  handleOnSuccess: (ncProvincialContributionId: number) => void
}

type UseUpdateNcProvincialContributionsArgs = QueryHandlers & {
  ncProvincialContributionId: number
}

type UseDeleteNcProvincialContributionsArgs = QueryHandlers & {
  ncProvincialContributionIds: number[]
  queries: NcProvincialContributionsSearch
}

async function getNcProvincialContributionsList(
  search: NcProvincialContributionsSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['nc-provincial-contributions'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getCurrentNcProvincialContributions(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['nc-provincial-contributions'].current.$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

export const currentNcProvincialContributionsQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentNcProvincialContributions', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCurrentNcProvincialContributions(crtPeriod, signal),
})

export function useCurrentNcProvincialContributions(crtPeriod?: string) {
  return useQuery(currentNcProvincialContributionsQueryOptions(crtPeriod))
}

async function getNcProvincialContributionsById(
  id: string,
  queries: NcProvincialContributionsDetailsServerQuery = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['nc-provincial-contributions'][':id'].$get(
    {
      param: { id },
      query: stringifyValues(queries),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createNcProvincialContributions(data: CreateNcProvincialContributions) {
  const api = getApi()

  const res = await api.crt['nc-provincial-contributions'].$post({
    json: data,
  })

  return await res.json()
}

async function updateNcProvincialContributions(
  ncProvincialContributionId: number,
  details: UpdateNcProvincialContributions,
) {
  const api = getApi()

  await api.crt['nc-provincial-contributions'][':id'].$patch({
    param: { id: String(ncProvincialContributionId) },
    json: details,
  })
}

async function deleteNcProvincialContributions(ncProvincialContributionIds: number[]) {
  const api = getApi()

  await api.crt['nc-provincial-contributions'].bulk.$delete({
    json: { ids: ncProvincialContributionIds },
  })
}

export function useNcProvincialContributionsList(queries: NcProvincialContributionsSearch) {
  return useQuery({
    queryKey: ['ncProvincialContributions', queries],
    queryFn: ({ signal }) => getNcProvincialContributionsList(queries, signal),
  })
}

export const ncProvincialContributionsByIdQueryOptions = (
  id: string,
  queries: NcProvincialContributionsDetailsServerQuery = {},
) => ({
  queryKey: ['ncProvincialContributionsById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getNcProvincialContributionsById(id, queries, signal),
})

export function useNcProvincialContributionsById(
  id: string,
  queries: NcProvincialContributionsDetailsServerQuery = {},
) {
  return useQuery(ncProvincialContributionsByIdQueryOptions(id, queries))
}

async function getNcProvincialContributionsDetailsForExport(
  id: string,
  queries: NcProvincialContributionsDetailsServerQuery = {},
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(queries)

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/nc-provincial-contributions/${id}/details/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export const ncProvincialContributionsDetailsExportQueryOptions = (
  id: string,
  queries: NcProvincialContributionsDetailsServerQuery = {},
) => ({
  queryKey: ['ncProvincialContributionsDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getNcProvincialContributionsDetailsForExport(id, queries, signal),
})

async function getNcProvincialContributionsDetailsForRawExport(id: string, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(
    `/crt/nc-provincial-contributions/${id}/details/raw-export`,
    signal,
  )
  return csvExport
}

export const ncProvincialContributionsDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['ncProvincialContributionsDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getNcProvincialContributionsDetailsForRawExport(id, signal),
})

export function useCreateNcProvincialContributions({
  queries,
  handleOnSuccess,
}: UseCreateNcProvincialContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['ncProvincialContributions', queries],
    mutationFn: createNcProvincialContributions,
    onSuccess: (ncProvincialContribution) => {
      handleOnSuccess(ncProvincialContribution.id)

      queryClient.invalidateQueries({
        queryKey: ['ncProvincialContributions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ncProvincialContributions.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let message: string
      switch (customParams?.type) {
        case NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERROR_TYPES.crtPeriodConflict:
          message = `${t('errors.ncProvincialContributions.createFailed', { ns: 'crt' })}: ${t(
            'errors.ncProvincialContributions.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          message = getFirstZodErrorMessage(
            issues,
            t,
            'errors.ncProvincialContributions.createFailed',
            'crt',
          )
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateNcProvincialContributions({
  ncProvincialContributionId,
  handleOnSuccess,
}: UseUpdateNcProvincialContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateNcProvincialContributions', ncProvincialContributionId],
    mutationFn: (details: UpdateNcProvincialContributions) =>
      updateNcProvincialContributions(ncProvincialContributionId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['ncProvincialContributionsById', String(ncProvincialContributionId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ncProvincialContributions.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.ncProvincialContributions.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteNcProvincialContributions({
  ncProvincialContributionIds,
  queries,
  handleOnSuccess,
}: UseDeleteNcProvincialContributionsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteNcProvincialContributions'],
    mutationFn: () => deleteNcProvincialContributions(ncProvincialContributionIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['ncProvincialContributions', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('ncProvincialContributions.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.ncProvincialContributions.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
